/**
 * Driver App Download Page
 * Replaces the PWA driver app — redirects chauffeurs to native mobile app.
 * Detects OS and shows appropriate store links.
 */
import React, { useEffect, useState } from 'react';
import { LogOut } from 'lucide-react';
import config from 'config';

const FALLBACK_PLAY = 'https://play.google.com/store/apps/details?id=fr.ygo.chauffeur';
const FALLBACK_IOS = 'https://testflight.apple.com/join/uCKRdksS';

function detectPlatform() {
  const ua = navigator.userAgent || navigator.vendor || '';
  if (/android/i.test(ua)) return 'android';
  if (/iPad|iPhone|iPod/.test(ua)) return 'ios';
  return 'desktop';
}

export default function DriverAppDownloadPage() {
  const [platform] = useState(detectPlatform);
  const [playUrl, setPlayUrl] = useState(FALLBACK_PLAY);
  const [iosUrl, setIosUrl] = useState(FALLBACK_IOS);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${config.API_URL}/api/app/mobile-settings`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('access_token') || localStorage.getItem('token')}` },
        });
        if (res.ok) {
          const d = await res.json();
          if (d.store_url_android) setPlayUrl(d.store_url_android);
          if (d.store_url_ios) setIosUrl(d.store_url_ios);
        }
      } catch {}
    })();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  return (
    <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center px-4 py-8" data-testid="driver-download-page">
      {/* Logo */}
      <div className="mb-8 text-center">
        <div className="w-20 h-20 rounded-2xl bg-[#C9A24D]/10 border border-[#C9A24D]/30 flex items-center justify-center mx-auto mb-4">
          <span className="text-[#C9A24D] text-3xl font-black tracking-tight">YG</span>
        </div>
        <h1 className="text-2xl font-bold text-white">Y GO Chauffeur</h1>
        <span className="inline-block mt-2 px-3 py-1 rounded-full bg-amber-500/15 text-amber-400 text-xs font-semibold">
          Version test beta
        </span>
      </div>

      {/* Message */}
      <div className="max-w-sm w-full text-center space-y-4 mb-8">
        <p className="text-white/60 text-sm leading-relaxed">
          L'application chauffeur Y GO est disponible sur mobile.
          Téléchargez l'app pour accéder à vos missions, suivre votre activité et recevoir les notifications en temps réel.
        </p>
      </div>

      {/* Download Buttons */}
      <div className="max-w-sm w-full space-y-3" data-testid="store-links">
        {/* Google Play */}
        <a
          href={playUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 w-full p-4 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition-all group"
          data-testid="play-store-link"
        >
          <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none">
              <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 01-.61-.92V2.734a1 1 0 01.609-.92z" fill="#4285F4"/>
              <path d="M17.556 8.236L5.148.87a1.003 1.003 0 00-1.539.944l10.183 10.186 3.764-3.764z" fill="#EA4335"/>
              <path d="M17.556 15.764L13.792 12 3.61 22.186a1 1 0 001.54.944l12.406-7.366z" fill="#34A853"/>
              <path d="M21.39 12c0-.385-.157-.733-.474-.965l-3.36-1.8-3.764 3.765 3.764 3.764 3.36-1.799c.317-.232.474-.58.474-.965z" fill="#FBBC04"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/40 text-[10px]">Télécharger sur</p>
            <p className="text-white text-base font-semibold group-hover:text-[#C9A24D] transition-colors">Google Play</p>
          </div>
          <svg className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </a>

        {/* App Store / TestFlight */}
        <a
          href={iosUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-4 w-full p-4 rounded-2xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] transition-all group"
          data-testid="app-store-link"
        >
          <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center shrink-0">
            <svg viewBox="0 0 24 24" className="w-7 h-7" fill="white">
              <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
            </svg>
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-white/40 text-[10px]">Tester sur</p>
            <p className="text-white text-base font-semibold group-hover:text-[#C9A24D] transition-colors">App Store (TestFlight)</p>
          </div>
          <svg className="w-5 h-5 text-white/30 group-hover:text-white/60 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </a>
      </div>

      {/* Auto-redirect hint for mobile */}
      {platform !== 'desktop' && (
        <p className="mt-6 text-white/30 text-xs text-center max-w-xs">
          {platform === 'android'
            ? "Vous êtes sur Android — cliquez sur Google Play ci-dessus."
            : "Vous êtes sur iOS — cliquez sur App Store ci-dessus."}
        </p>
      )}

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="mt-8 flex items-center gap-2 text-white/40 hover:text-white/70 text-sm transition-colors"
        data-testid="logout-btn"
      >
        <LogOut className="w-4 h-4" />
        Se déconnecter
      </button>
    </div>
  );
}
